/**
 * Manages Gemini Live API session resumption tokens per journal.
 * Tokens allow resuming a session without resending the full system prompt,
 * reducing token costs by ~90% on reconnections.
 */

interface ResumptionToken {
    token: string;
    journalId: string;
    createdAt: number;
    expiresAt: number;
}

const STORAGE_KEY = 'gemini_resumption_tokens';
const TOKEN_TTL_MS = 10 * 60 * 1000; // 10 minutes - matches Gemini's context cache duration
const MAX_STORED_JOURNALS = 10;

/**
 * Get a valid resumption token for a journal, if one exists.
 */
export function getResumptionToken(journalId: string): string | null {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (!stored) return null;

        const tokens: Record<string, ResumptionToken> = JSON.parse(stored);
        const token = tokens[journalId];

        if (!token) return null;
        if (Date.now() > token.expiresAt) {
            // Expired - clean up
            delete tokens[journalId];
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
            return null;
        }

        return token.token;
    } catch {
        return null;
    }
}

/**
 * Store a resumption token for a journal.
 */
export function storeResumptionToken(journalId: string, token: string): void {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        const tokens: Record<string, ResumptionToken> = stored ? JSON.parse(stored) : {};

        tokens[journalId] = {
            token,
            journalId,
            createdAt: Date.now(),
            expiresAt: Date.now() + TOKEN_TTL_MS,
        };

        // Cleanup old entries - keep only most recent journals
        const entries = Object.entries(tokens);
        if (entries.length > MAX_STORED_JOURNALS) {
            entries.sort((a, b) => b[1].createdAt - a[1].createdAt);
            const toKeep = entries.slice(0, MAX_STORED_JOURNALS);
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(toKeep)));
        } else {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
        }
    } catch {
        // SessionStorage not available - silent fail
    }
}

/**
 * Clear a resumption token for a journal (e.g., on error).
 */
export function clearResumptionToken(journalId: string): void {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (!stored) return;

        const tokens: Record<string, ResumptionToken> = JSON.parse(stored);
        delete tokens[journalId];
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    } catch {
        // Ignore
    }
}

/**
 * Clear all resumption tokens (e.g., on logout).
 */
export function clearAllResumptionTokens(): void {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // Ignore
    }
}
